import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCondominio } from '../contexts/CondominioContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { Building2, Users, Home, Car, Plus, Edit, Trash2 } from 'lucide-react';

const Apartamentos = () => {
  const { user } = useAuth();
  const { condominioSelecionado, getCondominioId, isAdministrador } = useCondominio();
  const [apartamentos, setApartamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingApartamento, setEditingApartamento] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    apartamento_bloco: '',
    apartamento_numero: '',
    apartamento_andar: '',
    apartamento_area: '',
    apartamento_quartos: '',
    apartamento_banheiros: '',
    apartamento_vagas_garagem: '',
    apartamento_observacoes: '',
    apartamento_valor_condominio: '',
    apartamento_ativo: true
  });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });

  useEffect(() => {
    const condominioId = getCondominioId();
    if (condominioId) loadApartamentos();
  }, [condominioSelecionado, pagination.page]);

  const loadApartamentos = async () => {
    try {
      setLoading(true);
      const condominioId = getCondominioId();
      if (!condominioId) {
        setApartamentos([]);
        return;
      }
      const params = new URLSearchParams();
      params.append('condominio_id', condominioId);
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      const response = await api.get(`/apartamentos?${params}`);
      const data = response.data.data;
      setApartamentos(data.apartamentos || []);
      const pag = data.pagination || {};
      setPagination(prev => ({ ...prev, total: pag.total || 0, pages: pag.pages || 1 }));
    } catch (error) {
      console.error('Erro ao carregar apartamentos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);
    
    try {
      const condominioId = getCondominioId();
      if (!condominioId) {
        setErrorMessage('Selecione um condomínio primeiro');
        return;
      }

      let valorCondominioNormalizado = null;

      if (formData.apartamento_valor_condominio) {
        const cleaned = formData.apartamento_valor_condominio
          .replace(/\s/g, '')
          .replace(/\./g, '')
          .replace(',', '.');
        const parsed = parseFloat(cleaned);
        if (!Number.isNaN(parsed)) {
          valorCondominioNormalizado = parsed;
        }
      }

      const data = {
        ...formData,
        apartamento_valor_condominio: valorCondominioNormalizado,
        condominio_id: condominioId
      };

      if (editingApartamento) {
        await api.put(`/apartamentos/${editingApartamento.apartamento_id}`, data);
      } else {
        await api.post('/apartamentos', data);
      }
      
      setShowModal(false);
      setEditingApartamento(null);
      resetForm();
      loadApartamentos();
    } catch (error) {
      console.error('Erro ao salvar apartamento:', error);
      
      // Tratar diferentes tipos de erro
      const apiErrors = error.response?.data?.errors;
      if (Array.isArray(apiErrors) && apiErrors.length > 0) {
        setErrorMessage(apiErrors.map(err => err.msg).filter(Boolean).join(', '));
      } else if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Erro interno do servidor. Tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrencyBR = (valor) => {
    if (valor === null || valor === undefined || valor === '') return '-';
    const numero = typeof valor === 'number' ? valor : parseFloat(valor);
    if (Number.isNaN(numero)) return '-';
    return numero.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  const formatValorForInput = (valor) => {
    if (valor === null || valor === undefined || valor === '') return '';
    const numero = Number(valor);
    if (Number.isNaN(numero)) {
      return String(valor);
    }
    return numero.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleEdit = (apartamento) => {
    setEditingApartamento(apartamento);
    setFormData({
      apartamento_bloco: apartamento.apartamento_bloco || '',
      apartamento_numero: apartamento.apartamento_numero || '',
      apartamento_andar: apartamento.apartamento_andar ?? '',
      apartamento_area: apartamento.apartamento_area || '',
      apartamento_quartos: apartamento.apartamento_quartos || '',
      apartamento_banheiros: apartamento.apartamento_banheiros || '',
      apartamento_vagas_garagem: apartamento.apartamento_vagas_garagem || '',
      apartamento_observacoes: apartamento.apartamento_observacoes || '',
      apartamento_valor_condominio: formatValorForInput(apartamento.apartamento_valor_condominio),
      apartamento_ativo: apartamento.apartamento_ativo !== false
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este apartamento?')) {
      try {
        await api.delete(`/apartamentos/${id}`);
        loadApartamentos();
      } catch (error) {
        console.error('Erro ao excluir apartamento:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      apartamento_bloco: '',
      apartamento_numero: '',
      apartamento_andar: '',
      apartamento_area: '',
      apartamento_quartos: '',
      apartamento_banheiros: '',
      apartamento_vagas_garagem: '',
      apartamento_observacoes: '',
      apartamento_valor_condominio: '',
      apartamento_ativo: true
    });
    setErrorMessage('');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;

    if (name === 'apartamento_valor_condominio') {
      // Permitir apenas números, ponto e vírgula
      newValue = newValue.replace(/[^\d.,]/g, '');
      // Garantir apenas um separador decimal
      const parts = newValue.split(/[.,]/);
      if (parts.length > 2) {
        newValue = `${parts[0]}.${parts.slice(1).join('')}`;
      } else if (parts.length === 2) {
        newValue = `${parts[0]},${parts[1]}`;
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="page-container">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Apartamentos</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gerencie os apartamentos do condomínio
          </p>
        </div>
        {!isAdministrador() || getCondominioId() ? (
          <button
            onClick={() => {
              setEditingApartamento(null);
              resetForm();
              setShowModal(true);
            }}
            className="btn btn-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Novo Apartamento
          </button>
        ) : (
          <div className="text-sm text-gray-500">
            Selecione um condomínio para gerenciar apartamentos
          </div>
        )}
      </div>

      {!getCondominioId() && isAdministrador() ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <Building2 className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Selecione um condomínio
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>Para visualizar e gerenciar apartamentos, selecione um condomínio no menu superior.</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Lista de Apartamentos */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="table-container">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Apartamento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bloco
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Andar
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Área
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor Condomínio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quartos
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vagas
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {apartamentos.map((apartamento) => (
                    <tr key={apartamento.apartamento_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Home className="h-5 w-5 text-gray-400 mr-3" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {apartamento.apartamento_numero}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {apartamento.apartamento_bloco}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {apartamento.apartamento_andar}º
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {apartamento.apartamento_area ? `${apartamento.apartamento_area}m²` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatCurrencyBR(apartamento.apartamento_valor_condominio)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {apartamento.apartamento_quartos || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {apartamento.apartamento_vagas_garagem || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          apartamento.apartamento_ativo 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {apartamento.apartamento_ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(apartamento)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(apartamento.apartamento_id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {apartamentos.length === 0 && !loading && (
            <div className="text-center py-12">
              <Home className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum apartamento</h3>
              <p className="mt-1 text-sm text-gray-500">
                Comece criando um novo apartamento.
              </p>
            </div>
          )}

          {/* Paginação */}
          {pagination.pages > 1 && (
            <div className="mt-4 flex items-center justify-between bg-white px-4 py-3 rounded-lg shadow">
              <div className="text-sm text-gray-700">
                Página {pagination.page} de {pagination.pages} · Total: {pagination.total} apartamentos
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page <= 1}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.pages, prev.page + 1) }))}
                  disabled={pagination.page >= pagination.pages}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal de Cadastro/Edição */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingApartamento ? 'Editar Apartamento' : 'Novo Apartamento'}
              </h3>
              
              {/* Exibir mensagem de erro */}
              {errorMessage && (
                <div className="mb-4 alert-error border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        Erro ao salvar apartamento
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>{errorMessage}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Bloco *</label>
                    <input
                      type="text"
                      name="apartamento_bloco"
                      value={formData.apartamento_bloco}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Número *</label>
                    <input
                      type="text"
                      name="apartamento_numero"
                      value={formData.apartamento_numero}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Andar *</label>
                    <input
                      type="number"
                      name="apartamento_andar"
                      value={formData.apartamento_andar}
                      onChange={handleInputChange}
                      required
                      min="0"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Área (m²)</label>
                    <input
                      type="number"
                      name="apartamento_area"
                      value={formData.apartamento_area}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Valor de Condomínio</label>
                    <input
                      type="text"
                      name="apartamento_valor_condominio"
                      value={formData.apartamento_valor_condominio}
                      onChange={handleInputChange}
                      placeholder="Ex: 500,00"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Informe o valor mensal cobrado desta unidade.</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Quartos</label>
                    <input
                      type="number"
                      name="apartamento_quartos"
                      value={formData.apartamento_quartos}
                      onChange={handleInputChange}
                      min="0"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Banheiros</label>
                    <input
                      type="number"
                      name="apartamento_banheiros"
                      value={formData.apartamento_banheiros}
                      onChange={handleInputChange}
                      min="0"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Vagas de Garagem</label>
                    <input
                      type="number"
                      name="apartamento_vagas_garagem"
                      value={formData.apartamento_vagas_garagem}
                      onChange={handleInputChange}
                      min="0"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Observações</label>
                    <textarea
                      name="apartamento_observacoes"
                      value={formData.apartamento_observacoes}
                      onChange={handleInputChange}
                      rows="3"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="apartamento_ativo"
                        checked={formData.apartamento_ativo}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700">Apartamento Ativo</span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingApartamento(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-4 py-2 rounded-md text-white ${
                      isSubmitting 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {isSubmitting ? 'Salvando...' : (editingApartamento ? 'Atualizar' : 'Criar')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Apartamentos;